/**
 * "Chart type" picker (screenshots/72 Chart tab + hover card, 81 behaviors 1–3 and 10).
 * - 25 types in groups; types that can't draw the dataset are dimmed with the reason and a one-click fix.
 * - Hover (250 ms intent) or focus opens a preview card that plays the user's own data in that type once
 *   (ECharts grow animation). Moving to the next tile keeps the card and crossfades its content.
 * - Space tries the hovered/focused type in the real widget (`onTry`, a store preview), Enter keeps it,
 *   Esc reverts. Arrow keys move through fitting types (roving tabindex).
 */
import { useEffect, useRef, useState } from "react";
import { CIcon } from "./icons";
import { prefersReducedMotion, recipes } from "../../motion";
import { buildOption, readPalette } from "./chartOptions";
import { CHART_TYPES, fits, pickerGroups, sizePlan, suggestType, whyNot, type ChartType, type Shape } from "./chartTypes";
import { ChartWidget, useECharts } from "./ChartWidget";
import type { ChartTheme } from "./themes";
import { paletteFromTheme } from "./themes";
import { useThemeVersion } from "../../theme/runtime";

export interface TypePickerProps {
  shape: Shape;
  /** The rows are sample data (no producer yet): previews say so; real rows don't. */
  sample?: boolean;
  value: ChartType;
  onChange: (t: ChartType) => void;          // Enter / click: a real, undoable change
  onTry: (t: ChartType | null) => void;      // Space: preview in the widget; null = revert
  onFix?: (t: ChartType) => void;            // dimmed tile's "switch data" action
  /** The fix button's text for a dimmed type (81 #10 "Group by ward instead"); default "Switch data for …". */
  fixLabel?: (t: ChartType) => string | null;
  theme?: ChartTheme;
  /** Search box above the picker (72 "Search 25 chart types"): hides tiles that don't match. */
  query?: string;
}

const HOVER_INTENT_MS = recipes.typeCard.hoverIntentMs;

export function TypePicker({ shape, sample, value, onChange, onTry, onFix, fixLabel, theme, query = "" }: TypePickerProps) {
  const suggested = suggestType(shape);
  const q = query.trim().toLowerCase();
  const groups = pickerGroups().map((g) => ({ ...g, types: g.types.filter((t) => !q || `${CHART_TYPES[t].label} ${g.group}`.toLowerCase().includes(q)) })).filter((g) => g.types.length);
  const order = groups.flatMap((g) => g.types).filter((t) => fits(t, shape));
  const refs = useRef(new Map<ChartType, HTMLButtonElement>());
  const [hover, setHover] = useState<{ type: ChartType; rect: DOMRect } | null>(null);
  const [trying, setTrying] = useState<ChartType | null>(null);
  const timer = useRef<number | null>(null);

  const open = (t: ChartType, el: HTMLElement, immediate = false) => {
    if (timer.current) window.clearTimeout(timer.current);
    const show = () => setHover({ type: t, rect: el.getBoundingClientRect() });
    if (immediate || hover) show();                        // card already open → swap, no second delay
    else timer.current = window.setTimeout(show, fits(t, shape) ? HOVER_INTENT_MS : recipes.typeNeeds.delay);   // 81 #10
  };
  const close = () => { if (timer.current) window.clearTimeout(timer.current); setHover(null); };
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  const move = (from: ChartType, step: number) => {
    const next = order[(order.indexOf(from) + step + order.length) % order.length];
    const el = refs.current.get(next);
    el?.focus();
    if (el) open(next, el, true);
  };

  return (
    <div className="type-picker" role="radiogroup" aria-label="Chart type" onMouseLeave={close}
      onKeyDown={(e) => { if (e.key === "Escape" && trying) { e.preventDefault(); setTrying(null); onTry(null); } }}>
      <div className="type-picker__groups">
        {groups.map(({ group, types }) => (
          <div key={group} className="type-picker__group">
            <span className="type-picker__label">{group}</span>
            <div className="type-picker__tiles">
              {types.map((t) => {
                const ok = fits(t, shape), on = t === value;
                return (
                  <button key={t} ref={(n) => { if (n) refs.current.set(t, n); else refs.current.delete(t); }}
                    role="radio" aria-checked={on} aria-disabled={!ok} tabIndex={on || (!order.includes(value) && t === order[0]) ? 0 : -1}
                    aria-label={ok ? CHART_TYPES[t].label : `${CHART_TYPES[t].label}. ${whyNot(t, shape) ?? ""}`}
                    aria-describedby={hover?.type === t ? "type-preview-card" : undefined}
                    className={`type-tile${on ? " is-on" : ""}${t === suggested ? " is-suggested" : ""}${trying === t ? " is-trying" : ""}`}
                    onMouseEnter={(e) => open(t, e.currentTarget)}
                    onFocus={(e) => open(t, e.currentTarget, true)}
                    onClick={() => { if (ok) { setTrying(null); onChange(t); } }}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); move(t, 1); }
                      if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); move(t, -1); }
                      if (e.key === " " && ok) { e.preventDefault(); setTrying(t); onTry(t); }
                      if (e.key === "Enter" && ok) { e.preventDefault(); setTrying(null); onChange(t); }
                    }}>
                    <CIcon name={CHART_TYPES[t].icon} />
                    <span>{CHART_TYPES[t].label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {hover && <PreviewCard type={hover.type} shape={shape} sample={sample} anchor={hover.rect} theme={theme} onFix={onFix} fixLabel={fixLabel} />}
    </div>
  );
}

/** The floating card. Fitting types play the user's data; dimmed types explain what they need and offer a fix. */
function PreviewCard({ type, shape, sample, anchor, theme, onFix, fixLabel }: { type: ChartType; shape: Shape; sample?: boolean; anchor: DOMRect; theme?: ChartTheme; onFix?: (t: ChartType) => void; fixLabel?: (t: ChartType) => string | null }) {
  const ok = fits(type, shape);
  const def = CHART_TYPES[type];
  return (
    <div id="type-preview-card" role="tooltip" className="type-preview" style={{ left: anchor.right + 12, top: Math.max(8, anchor.top - 28) }}>
      <div className="type-preview__head">
        <CIcon name={def.icon} /><strong>{def.label}</strong>
        <span className={`chip ${ok ? "chip--positive" : "chip--neutral"}`}>{ok ? "Fits your data" : "Needs other data"}</span>
      </div>
      {ok
        ? <MiniChart key={type} type={type} shape={shape} theme={theme} sample={sample} />
        : <div className="type-preview__need"><p>{whyNot(type, shape)}</p>{onFix && <button className="btn btn--small" onClick={() => onFix(type)}><CIcon name="arrow-left-right" />{fixLabel?.(type) ?? `Switch data for ${def.label}`}</button>}</div>}
      <p className="type-preview__best">Best for {def.best.charAt(0).toLowerCase() + def.best.slice(1)}.</p>
    </div>
  );
}

/** Plays once with the grow animation (keyed by type, so moving tiles re-plays the new type). */
function MiniChart({ type, shape, theme, sample }: { type: ChartType; shape: Shape; theme?: ChartTheme; sample?: boolean }) {
  // Number and Table aren't one ECharts option: show them the way the widget does.
  if (type === "number" || type === "table") return <div className="type-preview__chart type-preview__chart--html"><ChartWidget type={type} shape={shape} w={6} h={3} title={CHART_TYPES[type].label} theme={theme} source={sample ? { kind: "sample" } : { kind: "real", text: "" }} /></div>;
  return <MiniEChart type={type} shape={shape} theme={theme} />;
}
function MiniEChart({ type, shape, theme }: { type: ChartType; shape: Shape; theme?: ChartTheme }) {
  const el = useRef<HTMLDivElement>(null);
  const chart = useECharts(el, undefined, theme);
  const themeVersion = useThemeVersion();
  useEffect(() => {
    const palette = theme ? paletteFromTheme(readPalette(), theme) : readPalette();
    let opt: ReturnType<typeof buildOption>;
    try { opt = buildOption(type, shape, sizePlan(type, "M"), palette, !prefersReducedMotion()); } catch { return; }
    chart.current?.setOption({ ...opt, animationDuration: recipes.typeCard.grow, animationDelay: (i: number) => i * recipes.typeCard.stagger, tooltip: { show: false } }, { notMerge: true });
  }, [type, shape, themeVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  return <div ref={el} className="type-preview__chart" aria-hidden />;
}
